import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  FileText,
  CreditCard,
  Shield,
  Bell,
  CalendarCheck,
  Calendar as CalendarIcon,
  Smartphone,
  Facebook,
  Linkedin,
} from 'lucide-react';

const features = [
  { icon: FileText,    title: 'Document tracking',     desc: 'Passports, licenses, visas, IDs — every government deadline in one searchable list.' },
  { icon: CreditCard,  title: 'Subscription radar',    desc: 'Spot every auto-renewal before it hits your card. Cancel cleanly. Keep what you actually use.' },
  { icon: Shield,      title: 'Insurance policies',    desc: 'Health, auto, home, life, travel. Stay covered without the last-minute scramble.' },
  { icon: Bell,        title: 'Reminders that fit you', desc: 'Email + SMS at 30, 14, 7, and 1 day out — or any cadence you choose. Quiet by default.' },
  { icon: CalendarIcon,title: 'Calendar view',         desc: 'Month, week, agenda. Color-coded by category. Google + iCal sync on the way.' },
  { icon: Smartphone,  title: 'Multi-channel alerts',  desc: 'Pick the channel per renewal. Email for the small stuff, SMS when it really matters.' },
];

const plans = [
  { name: 'Free',       price: '$0',     period: 'forever',  features: ['3 renewals', 'Email reminders', 'Calendar view'],                                  cta: 'Start free',     featured: false },
  { name: 'Basic',      price: '$4.99',  period: '/ month',  features: ['10 renewals', 'Email reminders', 'Priority cadence', 'CSV export'],               cta: 'Choose Basic',   featured: false },
  { name: 'Pro',        price: '$9.99',  period: '/ month',  features: ['50 renewals', 'Email + SMS', 'Custom schedules', 'Calendar sync'],                cta: 'Choose Pro',     featured: true },
  { name: 'Enterprise', price: '$19.99', period: '/ month',  features: ['Unlimited', 'All channels', 'API access', 'Priority support'],                    cta: 'Contact sales',  featured: false },
];

const testimonials = [
  { quote: '“I missed my passport renewal twice before this. Got the email reminder a month out and walked into the office with time to spare.”', name: 'Marcus T.', meta: 'Brooklyn, NY · member since Jan' },
  { quote: '“Caught three auto-renewing subscriptions I had completely forgotten about. Paid for itself in the first week.”', name: 'Priya R.',  meta: 'Toronto, ON · member since Mar' },
  { quote: '“Our small team uses it to track every business license and insurance policy. The CSV export is a lifesaver at audit time.”', name: 'Lena V., Ops Lead', meta: 'Customer · 12 seats' },
];

const faqs = [
  { q: 'Is there really a free plan?',                     a: 'Yes. Free forever, up to 3 renewals with email reminders. No credit card required.' },
  { q: 'How early do I get reminded?',                     a: 'You choose. Default cadence is 30, 14, 7, and 1 day before. Customize per renewal on Pro.' },
  { q: 'Do you support SMS reminders?',                    a: 'Yes — on Pro and Enterprise. SMS is opt-in, US/Canada, and you can revoke anytime in Settings.' },
  { q: 'Can I import existing renewals?',                  a: 'CSV import is on the roadmap. For now, our quick-add form takes about 10 seconds per item.' },
  { q: 'Is my data private?',                              a: 'Encrypted in transit and at rest. We never sell data. We send only the reminders you opt in to receive.' },
  { q: 'What happens when I hit my plan limit?',           a: 'You can keep viewing existing renewals; new ones require an upgrade. Cancel anytime, no penalty.' },
];

const LandingPage = () => {
  return (
    <div className="bg-paper text-ink min-h-screen">

      {/* ============= NAV ============= */}
      <nav className="border-b border-ink-200 bg-paper/90 backdrop-blur sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-home-link">
            <div className="w-7 h-7 bg-ink flex items-center justify-center">
              <CalendarCheck className="w-4 h-4 text-paper" />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">Enota</span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm">
            <a href="#how" className="text-ink-600 hover:text-ink transition-colors">How it works</a>
            <a href="#features" className="text-ink-600 hover:text-ink transition-colors">Features</a>
            <a href="#pricing" className="text-ink-600 hover:text-ink transition-colors">Pricing</a>
            <a href="#faq" className="text-ink-600 hover:text-ink transition-colors">FAQ</a>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-ink-600 hover:text-ink transition-colors hidden sm:inline" data-testid="nav-login-btn">Log in</Link>
            <Link to="/register" className="btn btn-primary text-sm" data-testid="nav-signup-btn">Sign up free</Link>
          </div>
        </div>
      </nav>

      {/* ============= HERO ============= */}
      <section className="relative overflow-hidden border-b border-ink-200">
        <div className="absolute inset-0 grid-bg pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-24 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <p className="eyebrow mb-6" data-testid="hero-eyebrow">USA · CANADA · DEADLINES, HANDLED</p>
              <h1 className="h1" data-testid="hero-headline">
                Never miss<br/>
                <span className="text-respondent">a renewal again.</span>
              </h1>
              <p className="text-lg text-ink-600 mt-8 max-w-xl leading-relaxed">
                Enota keeps tabs on your documents, subscriptions, and policies — then nudges you by email or SMS before each one expires. Free to start. No credit card needed.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link to="/register" className="btn btn-respondent" data-testid="hero-get-started-btn">
                  Start tracking — it's free <ArrowRight className="w-[18px] h-[18px]" />
                </Link>
                <a href="#how" className="btn btn-outline" data-testid="hero-how-btn">
                  See how it works <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
              <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-ink-600">
                <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-success" />Free forever plan</span>
                <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-success" />Email + SMS reminders</span>
                <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-success" />Cancel anytime, no penalty</span>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="card-base p-8" data-testid="hero-card">
                <div className="eyebrow mb-3">UPCOMING · NEXT 90 DAYS</div>
                <ul className="divide-y divide-ink-200">
                  {[
                    { t: 'US Passport',          d: 'In 12 days',  c: 'DOCUMENT'},
                    { t: 'Auto Insurance',       d: 'In 28 days',  c: 'INSURANCE'},
                    { t: 'Adobe Creative Cloud', d: 'In 41 days',  c: 'SUBSCRIPTION'},
                    { t: 'Driver\'s License',    d: 'In 73 days',  c: 'DOCUMENT'},
                  ].map((r, i) => (
                    <li key={i} className="py-4 flex items-center justify-between gap-4">
                      <div>
                        <div className="font-medium text-ink">{r.t}</div>
                        <div className="font-mono text-[11px] text-ink-600 mt-0.5 tracking-wider">{r.c}</div>
                      </div>
                      <div className="font-mono text-sm text-respondent">{r.d}</div>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 border-t border-ink-200 pt-4 text-xs font-mono text-ink-600">
                  ENOTA://DASHBOARD · STATUS [LIVE]
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============= HOW IT WORKS ============= */}
      <section id="how" className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <p className="eyebrow mb-3">01 · HOW IT WORKS</p>
              <h2 className="h2 max-w-2xl">Three steps. Then quietly, on time, every time.</h2>
              <p className="text-ink-600 max-w-2xl mt-4">No spreadsheets. No sticky notes. No more 11pm "wait, when did that expire?" panics.</p>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-0 border border-ink-200">
            {[
              { n:'01', t:'Add a renewal',        d:'Title, category, due date, recurring or one-off. Ten seconds per item — bulk import is on the way.' },
              { n:'02', t:'Pick your cadence',    d:'Default reminders at 30, 14, 7, and 1 day out. Override channel and timing for any single renewal.' },
              { n:'03', t:'Get pinged on time',   d:'No spam. No upsells. The right reminder, the right channel, and a clean link to whatever you need to do next.' },
            ].map((s, i, arr) => (
              <div key={i} className={`p-10 border-ink-200 ${i < arr.length - 1 ? 'border-b md:border-b-0 md:border-r' : ''}`}>
                <div className="font-mono text-respondent text-sm mb-6">{s.n}</div>
                <h3 className="h3 mb-3">{s.t}</h3>
                <p className="text-ink-600 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============= WHY (DARK INVERTED) ============= */}
      <section className="py-24 bg-ink text-paper relative overflow-hidden">
        <div className="noise" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <p className="eyebrow mb-3 text-paper/70">02 · WHY ENOTA</p>
          <h2 className="h2 text-paper max-w-3xl mb-12">Built for people whose deadlines actually matter.</h2>
          <div className="grid md:grid-cols-2 gap-12">
            {[
              { i: FileText,    t: 'One ledger. Total clarity.',   d: 'Passports, leases, licenses, software — all in one searchable, sortable list. Export to CSV any time.' },
              { i: Bell,        t: 'Reminders that respect you.',  d: 'You choose the channel and cadence per renewal. No nudging, no upsells — only what you signed up for.' },
              { i: Shield,      t: 'Privacy by default.',          d: 'Encrypted in transit and at rest. We never sell data. SMS opt-ins are logged with timestamp and IP.' },
              { i: Smartphone,  t: 'Humans, on call.',             d: 'Real US-based support, no chatbots. Average reply time under four hours on business days.' },
            ].map((b, i) => (
              <div key={i} className="border-t border-paper/20 pt-8">
                <div className="text-respondent mb-4"><b.i className="w-7 h-7" /></div>
                <h3 className="h3 text-paper mb-2">{b.t}</h3>
                <p className="text-paper/70 text-sm leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============= FEATURES ============= */}
      <section id="features" className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="eyebrow mb-3">03 · FEATURES</p>
          <h2 className="h2 max-w-3xl mb-3">Track anything with an expiry date.</h2>
          <p className="text-ink-600 max-w-2xl mb-12">From your passport to your gym membership — if it expires, Enota can remind you about it.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border border-ink-200">
            {features.map((f, i) => {
              const I = f.icon;
              const isLastCol = (i + 1) % 3 === 0;
              const isLastRow = i >= features.length - 3;
              return (
                <div key={i} className={`p-8 border-ink-200 ${!isLastCol ? 'lg:border-r' : ''} ${(i + 1) % 2 === 0 ? '' : 'md:border-r lg:border-r-0'} ${!isLastRow ? 'border-b' : 'max-md:border-b'} ${i === features.length - 1 ? 'max-md:border-b-0' : ''} ${isLastCol ? 'md:border-r' : ''}`}>
                  <I className="w-6 h-6 text-respondent mb-5" />
                  <h3 className="h3 mb-2">{f.title}</h3>
                  <p className="text-ink-600 text-sm leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============= TESTIMONIALS ============= */}
      <section className="py-24 bg-cream border-y border-ink-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="eyebrow mb-3">04 · MEMBERS</p>
          <h2 className="h2 max-w-3xl mb-12">What people actually say.</h2>
          <div className="grid md:grid-cols-3 gap-0 border border-ink-200 bg-paper">
            {testimonials.map((t, i, arr) => (
              <div key={i} className={`p-10 flex flex-col justify-between min-h-[260px] border-ink-200 ${i < arr.length - 1 ? 'border-b md:border-b-0 md:border-r' : ''}`}>
                <p className="text-lg font-medium leading-snug">{t.quote}</p>
                <div className="mt-8">
                  <div className="font-semibold text-sm">{t.name}</div>
                  <div className="font-mono text-xs text-ink-600">{t.meta}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============= PRICING ============= */}
      <section id="pricing" className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="eyebrow mb-3">05 · PRICING</p>
          <h2 className="h2 max-w-3xl mb-3">Pick a plan. Skip a deadline disaster.</h2>
          <p className="text-ink-600 max-w-2xl mb-12">Start free. Upgrade only when you have more renewals to track. Cancel anytime, no penalty.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 border border-ink-200">
            {plans.map((p, i, arr) => (
              <div
                key={i}
                className={`p-8 flex flex-col border-ink-200 ${i < arr.length - 1 ? 'lg:border-r' : ''} ${i % 2 === 0 ? 'md:border-r' : ''} ${i < arr.length - 2 ? 'md:border-b lg:border-b-0' : ''} ${p.featured ? 'bg-ink text-paper' : ''}`}
              >
                <div className="flex items-baseline justify-between mb-1">
                  <h3 className={`h3 ${p.featured ? 'text-paper' : ''}`}>{p.name}</h3>
                  {p.featured && <span className="font-mono text-[10px] tracking-widest text-respondent">POPULAR</span>}
                </div>
                <div className="mb-6">
                  <span className={`font-display font-bold text-4xl tracking-tight ${p.featured ? 'text-paper' : ''}`}>{p.price}</span>
                  <span className={`text-sm ml-1 ${p.featured ? 'text-paper/60' : 'text-ink-600'}`}>{p.period}</span>
                </div>
                <ul className="space-y-2.5 mb-8 flex-1">
                  {p.features.map((f, fi) => (
                    <li key={fi} className={`flex items-start gap-2 text-sm ${p.featured ? 'text-paper/85' : 'text-ink-600'}`}>
                      <CheckCircle2 className={`w-4 h-4 flex-shrink-0 mt-0.5 ${p.featured ? 'text-respondent' : 'text-success'}`} />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/register"
                  className={`btn ${p.featured ? 'btn-respondent' : 'btn-outline'} w-full`}
                  data-testid={`pricing-${p.name.toLowerCase()}-btn`}
                >
                  {p.cta} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============= FAQ ============= */}
      <section id="faq" className="py-24 border-t border-ink-200">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <p className="eyebrow mb-3">06 · QUESTIONS</p>
          <h2 className="h2 mb-10">Frequently asked.</h2>
          <div className="divide-y divide-ink-200 border-y border-ink-200">
            {faqs.map((f, i) => (
              <details key={i} className="group py-6">
                <summary className="flex justify-between items-center cursor-pointer list-none">
                  <span className="text-lg font-semibold pr-6">{f.q}</span>
                  <span className="font-mono text-2xl text-ink-600 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-ink-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ============= FINAL CTA ============= */}
      <section className="py-20 border-t border-ink-200 bg-paper">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="h2 mb-6">Your deadlines aren't going away.<br/>Stop letting them ambush you.</h2>
          <Link to="/register" className="btn btn-respondent inline-flex" data-testid="footer-cta-btn">
            Track your first renewal — free <ArrowRight className="w-[18px] h-[18px]" />
          </Link>
        </div>
      </section>

      {/* ============= FOOTER ============= */}
      <footer className="bg-ink text-paper/70 pt-16 pb-10 border-t border-ink-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-10 mb-12">
            <div className="md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-7 h-7 bg-paper flex items-center justify-center">
                  <CalendarCheck className="w-4 h-4 text-ink" />
                </div>
                <span className="font-display font-bold text-lg text-paper">Enota</span>
              </div>
              <p className="text-sm leading-relaxed mb-5">
                Quiet, on-time renewal tracking for documents, subscriptions, and insurance.
              </p>
              <div className="flex items-center gap-3">
                <a href="https://www.facebook.com/renewalnote/" target="_blank" rel="noopener noreferrer" aria-label="Facebook"
                   className="w-9 h-9 border border-paper/20 flex items-center justify-center hover:bg-paper hover:text-ink transition-colors">
                  <Facebook className="w-4 h-4" />
                </a>
                <a href="https://www.linkedin.com/company/renewalnote/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"
                   className="w-9 h-9 border border-paper/20 flex items-center justify-center hover:bg-paper hover:text-ink transition-colors">
                  <Linkedin className="w-4 h-4" />
                </a>
              </div>
            </div>

            <div>
              <h4 className="eyebrow mb-4 text-paper/60">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-paper transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-paper transition-colors">Pricing</a></li>
                <li><Link to="/register" className="hover:text-paper transition-colors">Get started</Link></li>
                <li><Link to="/resources" className="hover:text-paper transition-colors">Resources</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="eyebrow mb-4 text-paper/60">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-paper transition-colors">About</Link></li>
                <li><Link to="/contact" className="hover:text-paper transition-colors">Contact</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="eyebrow mb-4 text-paper/60">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/terms" className="hover:text-paper transition-colors">Terms of Use</Link></li>
                <li><Link to="/privacy" className="hover:text-paper transition-colors">Privacy Policy</Link></li>
                <li><Link to="/california-privacy" className="hover:text-paper transition-colors">California Residents</Link></li>
                <li><Link to="/sms-terms" className="hover:text-paper transition-colors">SMS Terms</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-paper/15 pt-6 flex flex-col md:flex-row justify-between items-center gap-3">
            <p className="text-xs font-mono text-paper/50">© 2026 ENOTA · OFFER GADGET LLC · SAN JUAN, PR</p>
            <p className="text-xs font-mono text-paper/50">ENOTA://STATUS [ONLINE]</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
